'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import Header from './Header';
import Footer from './Footer';
import { Suspense } from 'react';
import PageTransition from './PageTransition';

// WhatsAppButton'u dinamik olarak import et
const WhatsAppButton = dynamic(() => import('../ui/WhatsAppButton'), {
  ssr: false,
  loading: () => (
    <div
      className="fixed bottom-6 right-6 z-[80] h-14 w-14 animate-pulse rounded-full bg-green-500/25"
      aria-hidden
    />
  ),
});

// Google Analytics için tip tanımlaması
declare global {
  interface Window {
    gtag?: (command: string, action: string, params: any) => void;
  }
}

// Layout component for the application
const Layout = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const [isLoaded, setIsLoaded] = useState(false);
  const [prevPathname, setPrevPathname] = useState('');
  
  // Track page changes for analytics and optimize page transitions
  useEffect(() => {
    // Sayfa değiştiğinde yükleme durumunu sıfırla ve yeni sayfa için tekrar başlat
    if (prevPathname !== pathname) {
      // Önceki sayfa değilse (ilk yükleme) veya sayfa değiştiyse
      setIsLoaded(false);
      
      // Yükleme efekti için kısa bir gecikme ve sonra yüklenmiş olarak işaretle
      const timer = setTimeout(() => {
        setIsLoaded(true);
        setPrevPathname(pathname);
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    // Prefetch visible links on the page for faster navigation
    const prefetchVisibleLinks = () => {
      // This will be handled by Next.js automatically for <Link> components
      // But could add custom prefetching logic here if needed
    };
    
    // Scroll to top on page change
    window.scrollTo(0, 0);
    
    // Track page view for analytics
    const trackPageView = () => {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('config', 'GA-MEASUREMENT-ID', {
          page_path: pathname,
        });
      }
    };
    
    prefetchVisibleLinks();
    trackPageView();
    
    // Clean up any event listeners
    return () => {
      // Clean up code here if needed
    };
  }, [pathname, prevPathname]);
  
  // Preload images for next page based on navigation likelihood
  useEffect(() => {
    // Helper function to preload an image
    const preloadImage = (src: string) => {
      const img = new Image();
      img.src = src;
    };
    
    // Preload key images based on current page
    if (pathname === '/') {
      // On homepage, preload the most likely next pages
      preloadImage('/images/products/barber-chair1.webp');
      preloadImage('/images/products/salon-desk1.webp');
    } else if (pathname === '/urunler') {
      preloadImage('/images/products/barber-chair1.webp');
    }
  }, [pathname]);
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className={`flex-grow ${pathname === '/' ? 'pt-0' : 'pt-16 md:pt-20'}`}>
        <Suspense fallback={<div className="min-h-screen bg-gray-50 animate-pulse"></div>}>
          <PageTransition isLoaded={isLoaded}>
            {pathname === '/' ? (
              children
            ) : (
              <div className="container mx-auto px-4">{children}</div>
            )}
          </PageTransition>
        </Suspense>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Layout; 